import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../../components/PageHeader';
import { useFeature } from '../../contexts/FeatureContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Settings, 
  Users,
  UserPlus,
  Building2,
  Palette,
  Shield,
  Globe,
  Copy,
  CheckCircle2,
  Clock,
  Briefcase,
  RefreshCw,
  Loader2,
  Crown,
  Save,
  MapPin,
  Hash,
  Mail,
  Phone,
  AlertTriangle,
  Medal,
  FileText,
  Database,
  Trash2,
  Plug,
  Key,
  Eye,
  EyeOff,
  ExternalLink,
  AlertCircle,
  Radar,
  X,
  Plus,
  Target,
  Brain,
  Zap,
  CheckCircle,
  XCircle,
  Sparkles
} from 'lucide-react';
import { useAuth, ROLES } from '../../contexts/AuthContext';
import { Switch } from '../../components/ui/switch';
import SlotTopUpModal from '../../components/modals/SlotTopUpModal';
import CustomerPortalButton from '../../components/CustomerPortalButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Provider logos/colors
const PROVIDER_STYLES = {
  anthropic: { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-700', name: 'Anthropic Claude' },
  gemini: { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-700', name: 'Google Gemini' },
  openai: { bg: 'bg-emerald-100', border: 'border-emerald-300', text: 'text-emerald-700', name: 'OpenAI GPT' },
};

// Organization AI Configuration Card Component
// Exported so the contractor-side WorkspaceSettings.jsx can mount the
// same card without duplicating ~400 lines of provider/model/test/save UI.
export function OrgAIConfigurationCard({ authToken, isSuperAdmin }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [providers, setProviders] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKeyChanged, setApiKeyChanged] = useState(false);
  
  const [config, setConfig] = useState({
    provider: 'anthropic',
    model: 'claude-sonnet-4-6',
    api_key: '',
    api_key_configured: false,
    api_key_preview: null,
    enabled: false,
    capture_thinking: true
  });

  // Fetch providers and org config
  useEffect(() => {
    const fetchData = async () => {
      if (!authToken) return;
      setLoading(true);
      
      try {
        // Fetch providers
        const providersRes = await fetch(`${API_URL}/api/llm/providers`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (providersRes.ok) {
          const data = await providersRes.json();
          setProviders(data.providers);
        }
        
        // Fetch org config
        const configRes = await fetch(`${API_URL}/api/llm/org-config`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (configRes.ok) {
          const data = await configRes.json();
          setConfig({
            provider: data.provider || 'anthropic',
            model: data.model || 'claude-sonnet-4-6',
            api_key: '',
            api_key_configured: data.api_key_configured,
            api_key_preview: data.api_key_preview,
            enabled: data.enabled,
            capture_thinking: data.capture_thinking !== false
          });
        }
      } catch (err) {
        console.error('Failed to fetch AI config:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [authToken]);

  const handleProviderChange = (provider) => {
    const defaultModel = providers?.[provider]?.default_model || '';
    setConfig(prev => ({ ...prev, provider, model: defaultModel }));
    setTestResult(null);
  };

  const handleModelChange = (model) => {
    setConfig(prev => ({ ...prev, model }));
    setTestResult(null);
  };

  const handleApiKeyChange = (value) => {
    setConfig(prev => ({ ...prev, api_key: value }));
    setApiKeyChanged(true);
    setTestResult(null);
  };

  const testConfiguration = async () => {
    if (!apiKeyChanged && !config.api_key_configured) {
      toast.error('Please enter an API key to test');
      return;
    }
    
    setTesting(true);
    setTestResult(null);
    
    try {
      const response = await fetch(`${API_URL}/api/llm/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          provider: config.provider,
          model: config.model,
          api_key: apiKeyChanged ? config.api_key : 'USE_EXISTING',
          prompt: "Say 'Configuration test successful!' and nothing else."
        })
      });
      
      const result = await response.json();
      setTestResult(result);
      
      if (result.success) {
        toast.success(`Test passed! ${result.latency_ms}ms`);
      } else {
        toast.error(`Test failed: ${result.error}`);
      }
    } catch (err) {
      toast.error('Failed to test configuration');
      setTestResult({ success: false, error: err.message });
    } finally {
      setTesting(false);
    }
  };

  const saveConfiguration = async () => {
    setSaving(true);
    
    try {
      const payload = {
        provider: config.provider,
        model: config.model,
        enabled: config.enabled,
        capture_thinking: config.capture_thinking
      };
      
      if (apiKeyChanged && config.api_key) {
        payload.api_key = config.api_key;
      }
      
      const response = await fetch(`${API_URL}/api/llm/org-config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success('AI configuration saved');
        setApiKeyChanged(false);
        // Refresh config
        const configRes = await fetch(`${API_URL}/api/llm/org-config`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (configRes.ok) {
          const data = await configRes.json();
          setConfig(prev => ({
            ...prev,
            api_key: '',
            api_key_configured: data.api_key_configured,
            api_key_preview: data.api_key_preview
          }));
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save configuration');
      }
    } catch (err) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const removeConfiguration = async () => {
    try {
      const response = await fetch(`${API_URL}/api/llm/org-config`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        toast.success('BYOM configuration removed');
        setConfig({
          provider: 'anthropic',
          model: 'claude-sonnet-4-6',
          api_key: '',
          api_key_configured: false,
          api_key_preview: null,
          enabled: false,
          capture_thinking: true
        });
        setApiKeyChanged(false);
      }
    } catch (err) {
      toast.error('Failed to remove configuration');
    }
  };

  const getAvailableModels = () => {
    if (!providers || !config.provider) return [];
    return providers[config.provider]?.models || [];
  };

  const providerStyle = PROVIDER_STYLES[config.provider] || PROVIDER_STYLES.gemini;

  if (loading) {
    return (
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="org-ai-config-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
              <Brain className="w-5 h-5 text-violet-700" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                AI Model Configuration (BYOM)
              </CardTitle>
              <CardDescription>
                Bring Your Own Model for Go/No-Go decisions
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Enable BYOM</span>
            <Switch
              data-testid="byom-enabled-switch"
              checked={config.enabled}
              onCheckedChange={(checked) => setConfig(prev => ({ ...prev, enabled: checked }))}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {config.enabled ? (
          <>
            {/* Provider Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">AI Provider</Label>
              <div className="grid grid-cols-3 gap-2">
                {providers && Object.entries(providers).map(([key, provider]) => {
                  const style = PROVIDER_STYLES[key];
                  const isSelected = config.provider === key;
                  
                  return (
                    <button
                      key={key}
                      data-testid={`provider-btn-${key}`}
                      onClick={() => handleProviderChange(key)}
                      className={`p-3 rounded-lg border transition-all text-left ${
                        isSelected 
                          ? `${style.bg} ${style.border} ring-1 ring-violet-500` 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className={`text-sm font-medium ${isSelected ? style.text : 'text-slate-700'}`}>
                        {provider.name}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Model Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Model</Label>
              <Select value={config.model} onValueChange={handleModelChange}>
                <SelectTrigger data-testid="model-select">
                  <SelectValue placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableModels().map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      <div className="flex items-center gap-2">
                        <span>{model.name}</span>
                        {model.supports_thinking && (
                          <Badge variant="outline" className="text-xs bg-violet-50 text-violet-700 border-violet-200">
                            Thinking
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* API Key */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">API Key</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    data-testid="org-api-key-input"
                    type={showApiKey ? 'text' : 'password'}
                    value={config.api_key}
                    onChange={(e) => handleApiKeyChange(e.target.value)}
                    placeholder={config.api_key_configured 
                      ? `Current: ${config.api_key_preview}` 
                      : 'Enter API key...'}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  variant="outline"
                  onClick={testConfiguration}
                  disabled={testing || (!apiKeyChanged && !config.api_key_configured)}
                  data-testid="test-org-config-btn"
                >
                  {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* Test Result */}
              {testResult && (
                <div className={`p-2 rounded-lg text-sm ${
                  testResult.success 
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {testResult.success ? (
                    <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Test passed ({testResult.latency_ms}ms)</span>
                  ) : (
                    <span className="flex items-center gap-1"><XCircle className="w-4 h-4" /> {testResult.error}</span>
                  )}
                </div>
              )}
            </div>

            {/* Capture Thinking Toggle - only for Claude */}
            {config.provider === 'anthropic' && (
              <div className="flex items-center justify-between p-3 bg-violet-50 rounded-lg border border-violet-200">
                <div>
                  <p className="text-sm font-medium text-violet-900">Capture Extended Thinking</p>
                  <p className="text-xs text-violet-700">
                    Save Claude's internal reasoning for transparency
                  </p>
                </div>
                <Switch
                  data-testid="capture-thinking-switch"
                  checked={config.capture_thinking}
                  onCheckedChange={(checked) => setConfig(prev => ({ ...prev, capture_thinking: checked }))}
                />
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between pt-4 border-t">
              {config.api_key_configured && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={removeConfiguration}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  data-testid="remove-byom-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Remove BYOM
                </Button>
              )}
              <Button
                onClick={saveConfiguration}
                disabled={saving}
                className="ml-auto bg-violet-600 hover:bg-violet-700"
                data-testid="save-org-ai-config-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Configuration
              </Button>
            </div>
          </>
        ) : (
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
              <div className="space-y-2">
                <div>
                  <h4 className="text-sm font-medium text-slate-700">BYOM Disabled</h4>
                  <p className="text-xs text-slate-500 mt-1">
                    When disabled, AI features are unavailable until your organization configures its own
                    provider key. Enable BYOM to use your own API key for premium models like Claude 4.6 Extended with thinking traces.
                  </p>
                </div>
                <div className="text-[11px] text-slate-500 border-t border-slate-200 pt-2">
                  <span className="font-semibold text-slate-600">Billing:</span> Each AI call is
                  metered against your prepaid AI credit balance. When BYOM is enabled, you pay the
                  LLM provider directly with your own key — your credits stretch significantly further
                  because we only charge a small platform fee for orchestration, audit, and scoring.
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ConsultancySettings() {
  const { user, authToken } = useAuth();
  // SAM.gov powers Marketplace Sentinel; hide Integrations tab when off.
  const sentinelEnabled = useFeature('sentinel');
  const [orgData, setOrgData] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [inviteRole, setInviteRole] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Organization Profile state (Super Admin only)
  const [orgProfile, setOrgProfile] = useState({
    entity_name: '',
    dba_names: '',
    uei: '',
    cage_code: '',
    duns_number: '',
    address: '',
    primary_poc_name: '',
    primary_poc_email: '',
    primary_poc_phone: '',
    socioeconomic_certifications: [],
    // Sentinel Preferences
    target_naics_codes: [],
    target_psc_codes: [],
    preferred_agencies: []
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [isSlotTopUpOpen, setIsSlotTopUpOpen] = useState(false);
  
  // NAICS/PSC input state
  const [naicsInput, setNaicsInput] = useState('');
  const [pscInput, setPscInput] = useState('');
  const [agencyInput, setAgencyInput] = useState('');
  
  // Data Security settings state (Super Admin only)
  const [securitySettings, setSecuritySettings] = useState({
    retention_days: 10,
    reminder_2day_enabled: true,
    reminder_1day_enabled: true,
    auto_purge_enabled: true
  });
  const [loadingSecuritySettings, setLoadingSecuritySettings] = useState(false);
  const [savingSecuritySettings, setSavingSecuritySettings] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  
  // SAM.gov Integration state
  const [samGovStatus, setSamGovStatus] = useState(null);
  const [samGovApiKey, setSamGovApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [isSavingApiKey, setIsSavingApiKey] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  
  // Socioeconomic certification options
  const certificationOptions = [
    { id: '8A', label: '8(a) Small Business' },
    { id: 'SDVOSB', label: 'Service-Disabled Veteran-Owned (SDVOSB)' },
    { id: 'VOSB', label: 'Veteran-Owned Small Business (VOSB)' },
    { id: 'WOSB', label: 'Women-Owned Small Business (WOSB)' },
    { id: 'EDWOSB', label: 'Economically Disadvantaged WOSB (EDWOSB)' },
    { id: 'HUBZONE', label: 'HUBZone' },
    { id: 'SDB', label: 'Small Disadvantaged Business (SDB)' },
    { id: 'SMALL_BUSINESS', label: 'Small Business' },
    { id: 'LARGE_BUSINESS', label: 'Large Business' }
  ];

  // Fetch organization data from workspace settings endpoint
  const fetchOrgData = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/workspace/settings`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOrgData(data.organization);
        setTeamMembers(data.team || []);
      }
    } catch (err) {
      console.error('Failed to fetch org data:', err);
    } finally {
      setLoading(false);
    }
  }, [authToken]);
  
  // Fetch organization profile for Prime Bidder data
  const fetchOrgProfile = useCallback(async () => {
    if (!authToken || !user?.organization_id) return;
    
    try {
      const response = await fetch(`${API_URL}/api/organizations/${user.organization_id}/prime-profile`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOrgProfile(prev => ({
          ...prev,
          entity_name: data.entity_name || '',
          uei: data.uei || '',
          cage_code: data.cage_code || '',
          duns_number: data.duns_number || '',
          socioeconomic_certifications: data.socioeconomic_status ? [data.socioeconomic_status] : []
        }));
        setProfileLoaded(true);
      }
    } catch (err) {
      console.error('Failed to fetch org profile:', err);
    }
  }, [authToken, user?.organization_id]);
  
  // Save organization profile
  const saveOrgProfile = async () => {
    if (!authToken || !user?.organization_id) return;
    
    setSavingProfile(true);
    try {
      // Determine primary socioeconomic status (first checked certification)
      const primaryStatus = orgProfile.socioeconomic_certifications[0] || null;
      
      const response = await fetch(`${API_URL}/api/organizations/${user.organization_id}/prime-profile`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          uei: orgProfile.uei || null,
          cage_code: orgProfile.cage_code || null,
          duns_number: orgProfile.duns_number || null,
          socioeconomic_status: primaryStatus
        })
      });
      
      if (response.ok) {
        toast.success('Organization profile saved successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save profile');
      }
    } catch (err) {
      console.error('Failed to save org profile:', err);
      toast.error('Failed to save organization profile');
    } finally {
      setSavingProfile(false);
    }
  };
  
  // Toggle certification checkbox
  const toggleCertification = (certId) => {
    setOrgProfile(prev => {
      const current = prev.socioeconomic_certifications || [];
      if (current.includes(certId)) {
        return { ...prev, socioeconomic_certifications: current.filter(c => c !== certId) };
      } else {
        return { ...prev, socioeconomic_certifications: [...current, certId] };
      }
    });
  };

  useEffect(() => {
    fetchOrgData();
  }, [fetchOrgData]);
  
  useEffect(() => {
    if (user?.role === ROLES.SUPER_ADMIN) {
      fetchOrgProfile();
    }
  }, [user?.role, fetchOrgProfile]);
  
  // Fetch Data Security settings
  const fetchSecuritySettings = useCallback(async () => {
    if (!authToken || user?.role !== ROLES.SUPER_ADMIN) return;
    
    setLoadingSecuritySettings(true);
    try {
      const response = await fetch(`${API_URL}/api/purge/settings`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSecuritySettings({
          retention_days: data.retention_days || 10,
          reminder_2day_enabled: data.reminder_2day_enabled ?? true,
          reminder_1day_enabled: data.reminder_1day_enabled ?? true,
          auto_purge_enabled: data.auto_purge_enabled ?? true
        });
      }
    } catch (err) {
      console.error('Failed to fetch security settings:', err);
    } finally {
      setLoadingSecuritySettings(false);
    }
  }, [authToken, user?.role]);
  
  // Fetch Audit logs
  const fetchAuditLogs = useCallback(async () => {
    if (!authToken || user?.role !== ROLES.SUPER_ADMIN) return;
    
    setLoadingAuditLogs(true);
    try {
      const response = await fetch(`${API_URL}/api/purge/audit-logs?limit=20`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.logs || []);
      }
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    } finally {
      setLoadingAuditLogs(false);
    }
  }, [authToken, user?.role]);
  
  // Save Data Security settings
  const saveSecuritySettings = async () => {
    setSavingSecuritySettings(true);
    try {
      const response = await fetch(`${API_URL}/api/purge/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(securitySettings)
      });
      
      if (response.ok) {
        toast.success('Data security settings saved');
        fetchAuditLogs(); // Refresh audit log
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Failed to save security settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSavingSecuritySettings(false);
    }
  };
  
  // Load security settings and audit logs when tab changes
  useEffect(() => {
    // Fetch SAM.gov status for all consultants
    fetchSamGovStatus();
    
    if (user?.role === ROLES.SUPER_ADMIN) {
      fetchSecuritySettings();
      fetchAuditLogs();
    }
  }, [user?.role, fetchSecuritySettings, fetchAuditLogs]);

  // Fetch SAM.gov integration status
  const fetchSamGovStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sentinel/integrations/sam-gov`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSamGovStatus(data);
      }
    } catch (error) {
      console.error('Error fetching SAM.gov status:', error);
    }
  };

  // Save SAM.gov API key
  const handleSaveApiKey = async () => {
    if (!samGovApiKey.trim()) {
      toast.error('Please enter an API key');
      return;
    }
    
    setIsSavingApiKey(true);
    try {
      const response = await fetch(`${API_URL}/api/sentinel/integrations/sam-gov`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ sam_gov_api_key: samGovApiKey })
      });
      
      if (response.ok) {
        toast.success('SAM.gov API key saved successfully!');
        setSamGovApiKey('');
        setShowApiKey(false);
        fetchSamGovStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save API key');
      }
    } catch (error) {
      toast.error('Failed to save API key');
    } finally {
      setIsSavingApiKey(false);
    }
  };

  // Test SAM.gov connection
  const handleTestConnection = async () => {
    setIsTestingConnection(true);
    try {
      const response = await fetch(`${API_URL}/api/sentinel/integrations/sam-gov/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast.success('Connection successful! SAM.gov API is working.');
        } else {
          toast.error(data.error || 'Connection test failed');
        }
      } else {
        toast.error('Connection test failed');
      }
    } catch (error) {
      toast.error('Failed to test connection');
    } finally {
      setIsTestingConnection(false);
    }
  };

  // Remove SAM.gov API key
  const handleRemoveApiKey = async () => {
    if (!window.confirm('Are you sure you want to remove the SAM.gov API key? This will disable live opportunity scanning.')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/sentinel/integrations/sam-gov`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        toast.success('API key removed. Sentinel will use demo mode.');
        fetchSamGovStatus();
      } else {
        toast.error('Failed to remove API key');
      }
    } catch (error) {
      toast.error('Failed to remove API key');
    }
  };

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Display values from real data
  const isSuperAdmin = user?.role === ROLES.SUPER_ADMIN;
  const displayOrgName = orgData?.name || 'Loading...';
  const displayOrgType = orgData?.type || 'CONSULTANCY';
  const displayOrgSlug = orgData?.slug || orgData?.id || 'loading';
  const displayContactEmail = user?.email || 'contact@company.com';
  const displayPlan = orgData?.subscription_plan || 'AGENCY';

  const getRoleLabel = (role) => {
    const labels = {
      'CONSULTANT': 'Consultant',
      'CONSULTANT_ASSOCIATE': 'Associate',
      'SUPER_ADMIN': 'Super Admin',
      'BID_MANAGER': 'Bid Manager',
      'REVIEWER': 'Reviewer'
    };
    return labels[role] || role;
  };

  if (loading) {
    return (
      <div data-testid="consultancy-settings-shell" className="min-h-screen bg-slate-50">
        <PageHeader 
          title="Consultancy Settings"
          description="Configure your consultancy workspace and team"
        />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="consultancy-settings-shell" className="min-h-screen bg-slate-50">
      <PageHeader 
        title="Consultancy Settings"
        description="Configure your consultancy workspace and team"
        action={
          <Button
            variant="outline"
            onClick={fetchOrgData}
            disabled={loading}
            className="border-slate-200"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />
      
      <div className="p-8">
        <div className="max-w-7xl mx-auto space-y-6">
        <Tabs defaultValue="general" className="w-full">
          <TabsList className="mb-6 bg-slate-100">
            <TabsTrigger value="general" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-600" data-testid="settings-general-tab">
              <Building2 className="w-4 h-4 mr-2" />
              General
            </TabsTrigger>
            {isSuperAdmin && (
              <TabsTrigger value="org-profile" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-600" data-testid="settings-org-profile-tab">
                <Crown className="w-4 h-4 mr-2" />
                Organization Profile
              </TabsTrigger>
            )}
            <TabsTrigger value="branding" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-600" data-testid="settings-branding-tab">
              <Palette className="w-4 h-4 mr-2" />
              Branding
            </TabsTrigger>
            <TabsTrigger value="team" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-600" data-testid="settings-team-tab">
              <Users className="w-4 h-4 mr-2" />
              Team
            </TabsTrigger>
            {sentinelEnabled && (
              <TabsTrigger value="integrations" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-600" data-testid="settings-integrations-tab">
                <Plug className="w-4 h-4 mr-2" />
                Integrations
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="data-security" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-600" data-testid="settings-security-tab">
                <Shield className="w-4 h-4 mr-2" />
                Data Security
              </TabsTrigger>
            )}
            <TabsTrigger value="billing" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-600" data-testid="settings-billing-tab">
              <Sparkles className="w-4 h-4 mr-2" />
              Billing
            </TabsTrigger>
            <TabsTrigger value="ai-config" className="data-[state=active]:bg-white data-[state=active]:text-slate-900 text-slate-600" data-testid="settings-ai-config-tab">
              <Zap className="w-4 h-4 mr-2" />
              AI Configuration
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Building2 className="w-5 h-5 text-teal-700" />
                    Consultancy Details
                  </CardTitle>
                  <CardDescription>
                    {isSuperAdmin ? 'Your flagship consultancy entity' : 'Basic information about your consultancy'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="consultancy-name">Organization Name</Label>
                    <Input 
                      id="consultancy-name" 
                      value={displayOrgName}
                      className="border-slate-200" 
                      disabled 
                      data-testid="org-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="consultancy-type">Organization Type</Label>
                    <div className="flex items-center gap-2">
                      <Input 
                        id="consultancy-type" 
                        value={displayOrgType === 'CONSULTANCY' ? 'Consultancy / Managed Services' : displayOrgType}
                        className="border-slate-200 bg-slate-50" 
                        disabled 
                      />
                      <Badge className="bg-blue-100 text-blue-700 whitespace-nowrap">
                        <Briefcase className="w-3 h-3 mr-1" />
                        {displayOrgType}
                      </Badge>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="consultancy-id">Organization Slug</Label>
                    <Input 
                      id="consultancy-id" 
                      value={displayOrgSlug}
                      className="border-slate-200 bg-slate-50" 
                      disabled
                      data-testid="org-slug-input" 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-email">Primary Contact Email</Label>
                    <Input 
                      id="contact-email" 
                      type="email" 
                      value={displayContactEmail}
                      className="border-slate-200" 
                      disabled 
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Subscription Plan</Label>
                      <Badge variant="secondary" className="text-sm">
                        {displayPlan?.replace('_', ' ') || 'AGENCY'}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <Label>Client Capacity</Label>
                      <p className="text-sm text-slate-600">
                        {orgData?.active_pursuits || 0} / {orgData?.pursuit_capacity || 10} pursuits
                      </p>
                    </div>
                  </div>
                  {isSuperAdmin && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-xs text-amber-700">
                        <strong>Note:</strong> This is your home organization. It appears on invoices and reviewer invitations you send to clients.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Globe className="w-5 h-5 text-teal-700" />
                    Client Visibility
                  </CardTitle>
                  <CardDescription>
                    Control how clients can find and link to you
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <h4 className="text-sm font-medium text-slate-700 mb-2">Type-Ahead Discovery</h4>
                      <p className="text-xs text-slate-500 mb-3">
                        Allow prospective clients to find your consultancy when creating accounts
                      </p>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-teal-500 rounded-full"></div>
                        <span className="text-xs text-teal-700 font-medium">Discoverable</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Organization Profile Tab - Super Admin Only */}
          {isSuperAdmin && (
            <TabsContent value="org-profile">
              <div className="space-y-6">
                {/* Core Identity Card */}
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          <Crown className="w-5 h-5 text-amber-600" />
                          Core Identity
                        </CardTitle>
                        <CardDescription>
                          Business identifiers used for Prime Bidder auto-population
                        </CardDescription>
                      </div>
                      <Badge className="bg-amber-100 text-amber-700">
                        Global KB Data
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2 space-y-2">
                        <Label htmlFor="legal-name">Legal Name</Label>
                        <Input
                          id="legal-name"
                          value={displayOrgName}
                          disabled
                          className="bg-slate-50"
                          data-testid="org-legal-name"
                        />
                        <p className="text-xs text-slate-500">Organization name from account registration</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="dba-names">DBA / Trade Names</Label>
                        <Input
                          id="dba-names"
                          placeholder="e.g., MNGC Consulting"
                          value={orgProfile.dba_names}
                          onChange={(e) => setOrgProfile(prev => ({ ...prev, dba_names: e.target.value }))}
                          data-testid="org-dba-names"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="uei" className="flex items-center gap-2">
                          <Hash className="w-3 h-3" />
                          UEI (Unique Entity ID)
                        </Label>
                        <Input
                          id="uei"
                          placeholder="12 alphanumeric characters"
                          value={orgProfile.uei}
                          onChange={(e) => setOrgProfile(prev => ({ ...prev, uei: e.target.value.toUpperCase().slice(0, 12) }))}
                          className="font-mono"
                          maxLength={12}
                          data-testid="org-uei"
                        />
                        <p className="text-xs text-slate-500">From SAM.gov registration</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="cage-code" className="flex items-center gap-2">
                          <Hash className="w-3 h-3" />
                          CAGE Code
                        </Label>
                        <Input
                          id="cage-code"
                          placeholder="5 alphanumeric"
                          value={orgProfile.cage_code}
                          onChange={(e) => setOrgProfile(prev => ({ ...prev, cage_code: e.target.value.toUpperCase().slice(0, 5) }))}
                          className="font-mono"
                          maxLength={5}
                          data-testid="org-cage-code"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="duns">DUNS Number (Legacy)</Label>
                        <Input
                          id="duns"
                          placeholder="9 digits"
                          value={orgProfile.duns_number}
                          onChange={(e) => setOrgProfile(prev => ({ ...prev, duns_number: e.target.value.replace(/\D/g, '').slice(0, 9) }))}
                          className="font-mono"
                          maxLength={9}
                          data-testid="org-duns"
                        />
                        <p className="text-xs text-slate-500">Deprecated but some contracts still reference</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="address" className="flex items-center gap-2">
                        <MapPin className="w-3 h-3" />
                        Principal Place of Business
                      </Label>
                      <Textarea
                        id="address"
                        placeholder="123 Main Street, Suite 100&#10;Washington, DC 20001"
                        value={orgProfile.address}
                        onChange={(e) => setOrgProfile(prev => ({ ...prev, address: e.target.value }))}
                        className="h-20"
                        data-testid="org-address"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Socioeconomic Certifications */}
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      <Shield className="w-5 h-5 text-emerald-600" />
                      Socioeconomic Certifications
                    </CardTitle>
                    <CardDescription>
                      Select all certifications that apply. Primary designation is used for set-aside compliance checks.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {certificationOptions.map((cert) => (
                        <div 
                          key={cert.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            orgProfile.socioeconomic_certifications.includes(cert.id)
                              ? 'bg-emerald-50 border-emerald-300'
                              : 'bg-white border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => toggleCertification(cert.id)}
                          data-testid={`cert-${cert.id.toLowerCase()}`}
                        >
                          <Checkbox
                            checked={orgProfile.socioeconomic_certifications.includes(cert.id)}
                            onCheckedChange={() => toggleCertification(cert.id)}
                          />
                          <Label className="cursor-pointer text-sm font-normal">
                            {cert.label}
                          </Label>
                          {orgProfile.socioeconomic_certifications[0] === cert.id && (
                            <Badge className="ml-auto bg-emerald-100 text-emerald-700 text-[10px]">Primary</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                      <p className="text-xs text-amber-700">
                        The <strong>first selected certification</strong> becomes your primary designation, used for set-aside compliance checks in the Go/No-Go dashboard.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Primary POC */}
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      <Mail className="w-5 h-5 text-blue-600" />
                      Primary Point of Contact
                    </CardTitle>
                    <CardDescription>
                      Contact information for bid notifications and proposal correspondence
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="poc-name">Name</Label>
                        <Input
                          id="poc-name"
                          placeholder="Jane Doe"
                          value={orgProfile.primary_poc_name}
                          onChange={(e) => setOrgProfile(prev => ({ ...prev, primary_poc_name: e.target.value }))}
                          data-testid="org-poc-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="poc-email" className="flex items-center gap-2">
                          <Mail className="w-3 h-3" />
                          Email
                        </Label>
                        <Input
                          id="poc-email"
                          type="email"
                          placeholder="bids@company.com"
                          value={orgProfile.primary_poc_email}
                          onChange={(e) => setOrgProfile(prev => ({ ...prev, primary_poc_email: e.target.value }))}
                          data-testid="org-poc-email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="poc-phone" className="flex items-center gap-2">
                          <Phone className="w-3 h-3" />
                          Phone
                        </Label>
                        <Input
                          id="poc-phone"
                          type="tel"
                          placeholder="(555) 123-4567"
                          value={orgProfile.primary_poc_phone}
                          onChange={(e) => setOrgProfile(prev => ({ ...prev, primary_poc_phone: e.target.value }))}
                          data-testid="org-poc-phone"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Sentinel Preferences - NAICS/PSC Codes */}
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                        <Target className="w-5 h-5 text-teal-700" />
                      </div>
                      <div>
                        <CardTitle className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          Sentinel Preferences
                        </CardTitle>
                        <CardDescription>
                          Configure NAICS and PSC codes for opportunity matching
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Target NAICS Codes */}
                    <div className="space-y-3">
                      <Label className="flex items-center gap-2">
                        <Hash className="w-3 h-3" />
                        Target NAICS Codes
                      </Label>
                      <p className="text-xs text-slate-500">
                        Add NAICS codes that match your core capabilities (e.g., 541512 for Computer Systems Design)
                      </p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter NAICS code (e.g., 541512)"
                          value={naicsInput}
                          onChange={(e) => setNaicsInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && naicsInput.trim()) {
                              e.preventDefault();
                              if (!orgProfile.target_naics_codes.includes(naicsInput.trim())) {
                                setOrgProfile(prev => ({
                                  ...prev,
                                  target_naics_codes: [...prev.target_naics_codes, naicsInput.trim()]
                                }));
                              }
                              setNaicsInput('');
                            }
                          }}
                          className="flex-1"
                          data-testid="naics-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            if (naicsInput.trim() && !orgProfile.target_naics_codes.includes(naicsInput.trim())) {
                              setOrgProfile(prev => ({
                                ...prev,
                                target_naics_codes: [...prev.target_naics_codes, naicsInput.trim()]
                              }));
                              setNaicsInput('');
                            }
                          }}
                          className="border-teal-200 text-teal-700 hover:bg-teal-50"
                          data-testid="add-naics-btn"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2 min-h-[32px]">
                        {orgProfile.target_naics_codes.map((code, idx) => (
                          <Badge 
                            key={idx} 
                            className="bg-teal-100 text-teal-700 border border-teal-200 px-3 py-1 text-sm font-mono"
                          >
                            {code}
                            <button
                              type="button"
                              onClick={() => setOrgProfile(prev => ({
                                ...prev,
                                target_naics_codes: prev.target_naics_codes.filter((_, i) => i !== idx)
                              }))}
                              className="ml-2 hover:text-teal-900"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                        {orgProfile.target_naics_codes.length === 0 && (
                          <span className="text-xs text-slate-400 italic">No NAICS codes added yet</span>
                        )}
                      </div>
                    </div>

                    {/* Target PSC Codes */}
                    <div className="space-y-3">
                      <Label className="flex items-center gap-2">
                        <FileText className="w-3 h-3" />
                        Target PSC Codes
                      </Label>
                      <p className="text-xs text-slate-500">
                        Add Product Service Codes for more precise matching (e.g., D310 for IT Cyber Security)
                      </p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter PSC code (e.g., D310)"
                          value={pscInput}
                          onChange={(e) => setPscInput(e.target.value.toUpperCase().slice(0, 4))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && pscInput.trim()) {
                              e.preventDefault();
                              if (!orgProfile.target_psc_codes.includes(pscInput.trim())) {
                                setOrgProfile(prev => ({
                                  ...prev,
                                  target_psc_codes: [...prev.target_psc_codes, pscInput.trim()]
                                }));
                              }
                              setPscInput('');
                            }
                          }}
                          className="flex-1"
                          data-testid="psc-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            if (pscInput.trim() && !orgProfile.target_psc_codes.includes(pscInput.trim())) {
                              setOrgProfile(prev => ({
                                ...prev,
                                target_psc_codes: [...prev.target_psc_codes, pscInput.trim()]
                              }));
                              setPscInput('');
                            }
                          }}
                          className="border-teal-200 text-teal-700 hover:bg-teal-50"
                          data-testid="add-psc-btn"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2 min-h-[32px]">
                        {orgProfile.target_psc_codes.map((code, idx) => (
                          <Badge 
                            key={idx} 
                            className="bg-purple-100 text-purple-700 border border-purple-200 px-3 py-1 text-sm font-mono"
                          >
                            {code}
                            <button
                              type="button"
                              onClick={() => setOrgProfile(prev => ({
                                ...prev,
                                target_psc_codes: prev.target_psc_codes.filter((_, i) => i !== idx)
                              }))}
                              className="ml-2 hover:text-purple-900"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                        {orgProfile.target_psc_codes.length === 0 && (
                          <span className="text-xs text-slate-400 italic">No PSC codes added yet</span>
                        )}
                      </div>
                    </div>

                    {/* Preferred Agencies */}
                    <div className="space-y-3">
                      <Label className="flex items-center gap-2">
                        <Building2 className="w-3 h-3" />
                        Preferred Agencies (Optional)
                      </Label>
                      <p className="text-xs text-slate-500">
                        Add agency abbreviations to prioritize (e.g., DOD, DHS, VA)
                      </p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter agency (e.g., DOD)"
                          value={agencyInput}
                          onChange={(e) => setAgencyInput(e.target.value.toUpperCase())}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && agencyInput.trim()) {
                              e.preventDefault();
                              if (!orgProfile.preferred_agencies.includes(agencyInput.trim())) {
                                setOrgProfile(prev => ({
                                  ...prev,
                                  preferred_agencies: [...prev.preferred_agencies, agencyInput.trim()]
                                }));
                              }
                              setAgencyInput('');
                            }
                          }}
                          className="flex-1"
                          data-testid="agency-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            if (agencyInput.trim() && !orgProfile.preferred_agencies.includes(agencyInput.trim())) {
                              setOrgProfile(prev => ({
                                ...prev,
                                preferred_agencies: [...prev.preferred_agencies, agencyInput.trim()]
                              }));
                              setAgencyInput('');
                            }
                          }}
                          className="border-teal-200 text-teal-700 hover:bg-teal-50"
                          data-testid="add-agency-btn"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2 min-h-[32px]">
                        {orgProfile.preferred_agencies.map((agency, idx) => (
                          <Badge 
                            key={idx} 
                            className="bg-slate-100 text-slate-700 border border-slate-200 px-3 py-1 text-sm"
                          >
                            {agency}
                            <button
                              type="button"
                              onClick={() => setOrgProfile(prev => ({
                                ...prev,
                                preferred_agencies: prev.preferred_agencies.filter((_, i) => i !== idx)
                              }))}
                              className="ml-2 hover:text-slate-900"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                        {orgProfile.preferred_agencies.length === 0 && (
                          <span className="text-xs text-slate-400 italic">No agencies specified - will match all agencies</span>
                        )}
                      </div>
                    </div>

                    {/* Match Scoring Info */}
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <h4 className="text-sm font-medium text-slate-700 mb-2">How Matching Works</h4>
                      <ul className="text-xs text-slate-600 space-y-1">
                        <li className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full bg-amber-400"></span>
                          <strong>Gold Match (50%):</strong> NAICS + PSC code match
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full bg-slate-400"></span>
                          <strong>Silver Match (40%):</strong> NAICS + preferred agency match
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full bg-orange-400"></span>
                          <strong>Bronze Match (30%):</strong> NAICS code only
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                {/* Save Button */}
                <div className="flex justify-end">
                  <Button
                    onClick={saveOrgProfile}
                    disabled={savingProfile}
                    className="bg-teal-600 hover:bg-teal-700"
                    data-testid="save-org-profile-btn"
                  >
                    {savingProfile ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Organization Profile
                  </Button>
                </div>

                {/* Info Notice */}
                <Card className="border-teal-200 bg-teal-50/50 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-teal-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-teal-800">Global Knowledge Base Integration</p>
                        <p className="text-sm text-teal-700 mt-1">
                          This data is used to auto-populate the "Lead Prime Bidder" fields in the Suggest & Confirm Modal 
                          for all new pursuits. Changes here are reflected instantly in future bids.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          <TabsContent value="branding">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Palette className="w-5 h-5 text-teal-700" />
                  Custom Branding
                </CardTitle>
                <CardDescription>
                  Customize your consultancy's appearance for client portals
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center justify-center py-12">
                  <Palette className="w-12 h-12 text-slate-300 mb-3" />
                  <p className="text-slate-500 text-sm">Branding customization coming soon</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="team">
            <div className="space-y-6">
              {/* Invite New Member via Link - Share registration link */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <UserPlus className="w-5 h-5 text-teal-700" />
                    Invite Team Member
                  </CardTitle>
                  <CardDescription>
                    Share an invite link for team members to join your consultancy
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <Label>Select Role for Invite Link</Label>
                    <div className="flex items-center gap-4">
                      <Select value={inviteRole} onValueChange={setInviteRole}>
                        <SelectTrigger className="w-48 border-slate-200" data-testid="team-invite-role">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          {/* Only BID_MANAGER and REVIEWER allowed for Consultancy team */}
                          <SelectItem value="BID_MANAGER">Bid Manager</SelectItem>
                          <SelectItem value="REVIEWER">Reviewer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {inviteRole && (
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <Label className="text-sm font-medium text-slate-700 mb-2 block">Invite Link</Label>
                      <div className="flex items-center gap-2">
                        <Input 
                          readOnly
                          value={`${window.location.origin}/register/team?org=${displayOrgSlug}&role=${inviteRole.toLowerCase()}`}
                          className="border-slate-200 bg-white font-mono text-sm"
                          data-testid="team-invite-link"
                        />
                        <Button 
                          variant="outline"
                          className="shrink-0"
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/register/team?org=${displayOrgSlug}&role=${inviteRole.toLowerCase()}`);
                          }}
                          data-testid="copy-invite-link"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Link
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        Share this link with your team member. They will sign up through the "Get Started" page and automatically join your consultancy as a {inviteRole === 'BID_MANAGER' ? 'Bid Manager' : 'Reviewer'}.
                      </p>
                    </div>
                  )}
                  
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-700">
                      <strong>Note:</strong> Team members can only be assigned Bid Manager or Reviewer roles. Contractor Admin is not available for consultancy teams.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Team Members Table */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Consultancy Team ({teamMembers.length} members)
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {/* Table header */}
                  <div className="grid grid-cols-5 gap-4 px-6 py-3 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    <div className="col-span-2">Member</div>
                    <div>Role</div>
                    <div>Status</div>
                    <div>Actions</div>
                  </div>
                  
                  {teamMembers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <Users className="w-12 h-12 text-slate-300 mb-3" />
                      <p className="text-slate-500 text-sm">No team members found</p>
                      <p className="text-slate-400 text-xs mt-1">
                        Invite Bid Managers or Reviewers to join your consultancy
                      </p>
                    </div>
                  ) : (
                    teamMembers.map((member) => (
                      <div key={member.id} className="grid grid-cols-5 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 transition-colors">
                        <div className="col-span-2 flex items-center gap-3">
                          <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-teal-700">
                              {(member.name || member.email || '?').charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900">
                                {member.name || member.email}
                              </p>
                              {member.is_owner && (
                                <Badge variant="outline" className="text-xs border-amber-300 text-amber-700">Owner</Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-500">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <Badge className={
                            member.role === 'CONSULTANT' || member.role === 'SUPER_ADMIN'
                              ? 'bg-purple-100 text-purple-700'
                              : member.role === 'BID_MANAGER'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-slate-100 text-slate-600'
                          }>
                            {getRoleLabel(member.role)}
                          </Badge>
                        </div>
                        <div className="flex items-center">
                          {member.is_active !== false ? (
                            <span className="flex items-center gap-1.5 text-sm text-emerald-600">
                              <CheckCircle2 className="w-4 h-4" />
                              Active
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5 text-sm text-amber-600">
                              <Clock className="w-4 h-4" />
                              Pending
                            </span>
                          )}
                        </div>
                        <div className="flex items-center text-sm text-slate-400">
                          —
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              {/* Data Silo Notice */}
              <Card className="border-amber-200 bg-amber-50/50 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Data Silo Protection</p>
                      <p className="text-sm text-amber-700 mt-1">
                        As a Consultant, you cannot access your clients' Team Management or Invitations settings. 
                        Only the Contractor Admin (data owner) can manage their organization's team members.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Integrations Tab - Available to all Consultants */}
          {sentinelEnabled && (
          <TabsContent value="integrations">
              <div className="space-y-6">
                {/* SAM.gov Integration */}
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                          <Radar className="w-5 h-5 text-teal-700" />
                        </div>
                        <div>
                          <CardTitle className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            SAM.gov Integration
                          </CardTitle>
                          <CardDescription>
                            Connect to SAM.gov for live opportunity scanning
                          </CardDescription>
                        </div>
                      </div>
                      <Badge className={samGovStatus?.has_api_key ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                        {samGovStatus?.has_api_key ? 'Connected' : 'Demo Mode'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Status Info */}
                    {samGovStatus && (
                      <div className="grid grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Status</p>
                          <p className="text-sm font-medium text-slate-900 capitalize">{samGovStatus.status}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Daily Usage</p>
                          <p className="text-sm font-medium text-slate-900">
                            {samGovStatus.daily_usage} / {samGovStatus.daily_limit} calls
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Last Scan</p>
                          <p className="text-sm font-medium text-slate-900">
                            {samGovStatus.last_sync ? formatDate(samGovStatus.last_sync) : 'Never'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Next Scheduled</p>
                          <p className="text-sm font-medium text-slate-900">
                            {samGovStatus.next_scan ? formatDate(samGovStatus.next_scan) : 'Pending first scan'}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* API Key Input */}
                    {!samGovStatus?.has_api_key ? (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="sam-gov-api-key">SAM.gov API Key</Label>
                          <div className="flex gap-2">
                            <div className="relative flex-1">
                              <Input 
                                id="sam-gov-api-key"
                                type={showApiKey ? 'text' : 'password'}
                                placeholder="Enter your SAM.gov API key"
                                value={samGovApiKey}
                                onChange={(e) => setSamGovApiKey(e.target.value)}
                                className="border-slate-200 pr-10"
                                data-testid="sam-gov-api-key-input"
                              />
                              <button
                                type="button"
                                onClick={() => setShowApiKey(!showApiKey)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                              >
                                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                            <Button 
                              onClick={handleSaveApiKey}
                              disabled={isSavingApiKey || !samGovApiKey.trim()}
                              className="bg-teal-700 hover:bg-teal-800 text-white"
                              data-testid="save-api-key-btn"
                            >
                              {isSavingApiKey ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Key className="w-4 h-4 mr-2" />
                                  Save Key
                                </>
                              )}
                            </Button>
                          </div>
                          <p className="text-xs text-slate-500">
                            Your API key is encrypted and stored securely.
                          </p>
                        </div>

                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <h4 className="text-sm font-medium text-blue-900">How to get a SAM.gov API Key</h4>
                              <ol className="mt-2 text-xs text-blue-800 space-y-1 list-decimal list-inside">
                                <li>Visit <a href="https://api.sam.gov" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">api.sam.gov</a></li>
                                <li>Create an account or sign in</li>
                                <li>Navigate to "API Keys" in your account settings</li>
                                <li>Generate a new public API key for "Opportunities API"</li>
                                <li>Copy and paste the key above</li>
                              </ol>
                              <a 
                                href="https://api.sam.gov" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 mt-2 text-xs text-blue-700 hover:text-blue-800 font-medium"
                              >
                                Go to SAM.gov API Portal
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex items-center gap-3">
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                            <div>
                              <p className="text-sm font-medium text-green-900">API Key Configured</p>
                              <p className="text-xs text-green-700">Your SAM.gov integration is active</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="outline"
                              size="sm"
                              onClick={handleTestConnection}
                              disabled={isTestingConnection}
                              className="border-green-300 text-green-700 hover:bg-green-100"
                              data-testid="test-connection-btn"
                            >
                              {isTestingConnection ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <RefreshCw className="w-4 h-4 mr-1" />
                                  Test
                                </>
                              )}
                            </Button>
                            <Button 
                              variant="outline"
                              size="sm"
                              onClick={handleRemoveApiKey}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                              data-testid="remove-api-key-btn"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Remove
                            </Button>
                          </div>
                        </div>

                        <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                          <h4 className="text-sm font-medium text-slate-700 mb-2">Integration Features</h4>
                          <ul className="text-xs text-slate-600 space-y-1">
                            <li className="flex items-center gap-2">
                              <CheckCircle2 className="w-3 h-3 text-teal-600" />
                              Automatic opportunity scanning every 4 hours
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle2 className="w-3 h-3 text-teal-600" />
                              Real-time matching against your NAICS/PSC codes
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle2 className="w-3 h-3 text-teal-600" />
                              Alert notifications for high-value opportunities
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle2 className="w-3 h-3 text-teal-600" />
                              One-click pursuit creation from matches
                            </li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Future Integrations Placeholder */}
                <Card className="border-slate-200 shadow-sm border-dashed">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-slate-400 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      <Plug className="w-5 h-5" />
                      More Integrations Coming Soon
                    </CardTitle>
                    <CardDescription>
                      GovWin, FPDS, USA Spending, and more
                    </CardDescription>
                  </CardHeader>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* AI Configuration (BYOM) — its own tab so it's discoverable
              regardless of whether Sentinel is enabled, and decoupled
              from the Sentinel-gated Integrations tab. */}
          <TabsContent value="ai-config">
            <div className="space-y-6 max-w-4xl">
              <OrgAIConfigurationCard authToken={authToken} isSuperAdmin={isSuperAdmin} />
            </div>
          </TabsContent>
          
          {/* Data Security Tab - Super Admin Only */}
          {isSuperAdmin && (
            <TabsContent value="data-security">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Retention Policy Settings */}
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      <Clock className="w-5 h-5 text-teal-700" />
                      Document Retention Policy
                    </CardTitle>
                    <CardDescription>
                      Configure automatic document purge settings for compliance and security
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {loadingSecuritySettings ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                      </div>
                    ) : (
                      <>
                        {/* Retention Days */}
                        <div className="space-y-2">
                          <Label htmlFor="retention_days" className="text-sm font-medium text-slate-700">
                            Retention Period (Days)
                          </Label>
                          <div className="flex items-center gap-3">
                            <Input
                              id="retention_days"
                              type="number"
                              min={3}
                              max={365}
                              value={securitySettings.retention_days}
                              onChange={(e) => setSecuritySettings(prev => ({
                                ...prev,
                                retention_days: Math.max(3, parseInt(e.target.value) || 10)
                              }))}
                              className="w-24"
                              data-testid="retention-days-input"
                            />
                            <span className="text-sm text-slate-500">days (minimum 3)</span>
                          </div>
                          <p className="text-xs text-slate-500">
                            Documents older than this will be automatically purged
                          </p>
                        </div>
                        
                        {/* Reminder Settings */}
                        <div className="space-y-3 pt-4 border-t border-slate-100">
                          <Label className="text-sm font-medium text-slate-700">Expiry Reminders</Label>
                          
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id="reminder_2day"
                              checked={securitySettings.reminder_2day_enabled}
                              onCheckedChange={(checked) => setSecuritySettings(prev => ({
                                ...prev,
                                reminder_2day_enabled: checked
                              }))}
                            />
                            <Label htmlFor="reminder_2day" className="text-sm text-slate-600 cursor-pointer">
                              2-day warning before expiry
                            </Label>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id="reminder_1day"
                              checked={securitySettings.reminder_1day_enabled}
                              onCheckedChange={(checked) => setSecuritySettings(prev => ({
                                ...prev,
                                reminder_1day_enabled: checked
                              }))}
                            />
                            <Label htmlFor="reminder_1day" className="text-sm text-slate-600 cursor-pointer">
                              1-day warning before expiry
                            </Label>
                          </div>
                        </div>
                        
                        {/* Auto Purge Toggle */}
                        <div className="space-y-3 pt-4 border-t border-slate-100">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id="auto_purge"
                              checked={securitySettings.auto_purge_enabled}
                              onCheckedChange={(checked) => setSecuritySettings(prev => ({
                                ...prev,
                                auto_purge_enabled: checked
                              }))}
                            />
                            <Label htmlFor="auto_purge" className="text-sm font-medium text-slate-700 cursor-pointer">
                              Enable automatic purge
                            </Label>
                          </div>
                          <p className="text-xs text-slate-500 pl-6">
                            {securitySettings.auto_purge_enabled 
                              ? 'Documents will be automatically deleted when retention period expires'
                              : 'Documents will only be deleted manually - you will still receive expiry warnings'}
                          </p>
                        </div>
                        
                        {/* Save Button */}
                        <div className="pt-4">
                          <Button
                            onClick={saveSecuritySettings}
                            disabled={savingSecuritySettings}
                            className="bg-teal-700 hover:bg-teal-800"
                            data-testid="save-security-settings-btn"
                          >
                            {savingSecuritySettings ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Save className="w-4 h-4 mr-2" />
                            )}
                            Save Retention Settings
                          </Button>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
                
                {/* Security Info Card */}
                <div className="space-y-6">
                  <Card className="border-teal-200 bg-teal-50/50 shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-teal-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-teal-800">Zero-Leak Architecture</p>
                          <p className="text-sm text-teal-700 mt-1">
                            When documents are purged, they are permanently deleted from:
                          </p>
                          <ul className="text-sm text-teal-700 mt-2 space-y-1 list-disc pl-4">
                            <li>Document metadata (pursuit_kb_documents)</li>
                            <li>Knowledge base chunks (pursuit_kb_chunks)</li>
                            <li>File storage (GridFS)</li>
                          </ul>
                          <p className="text-sm text-teal-700 mt-2">
                            All deletions are logged in the audit trail below.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Audit Log */}
                  <Card className="border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          <Briefcase className="w-5 h-5 text-teal-700" />
                          Deletion Audit Log
                        </CardTitle>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={fetchAuditLogs}
                          disabled={loadingAuditLogs}
                        >
                          <RefreshCw className={`w-4 h-4 ${loadingAuditLogs ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                      <CardDescription>
                        Recent document purge operations
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {loadingAuditLogs ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                        </div>
                      ) : auditLogs.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                          <Shield className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No deletion operations recorded yet</p>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-[400px] overflow-y-auto">
                          {auditLogs.map((log) => (
                            <div 
                              key={log.id} 
                              className="p-3 bg-slate-50 rounded-lg border border-slate-100"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <Badge 
                                      className={
                                        log.trigger_type?.includes('AUTO') 
                                          ? 'bg-amber-100 text-amber-700 border-amber-200'
                                          : 'bg-blue-100 text-blue-700 border-blue-200'
                                      }
                                    >
                                      {log.trigger_type?.includes('AUTO') ? 'Auto' : 'Manual'}
                                    </Badge>
                                    <span className="text-sm font-medium text-slate-700">
                                      {log.action?.replace(/_/g, ' ')}
                                    </span>
                                  </div>
                                  <p className="text-xs text-slate-500 mt-1 truncate">
                                    {log.target_name || log.target_id}
                                  </p>
                                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                                    <span>{log.documents_deleted} docs</span>
                                    <span>{log.chunks_deleted} chunks</span>
                                    <span>{log.files_deleted} files</span>
                                  </div>
                                </div>
                                <span className="text-xs text-slate-400 whitespace-nowrap">
                                  {log.timestamp ? new Date(log.timestamp).toLocaleDateString() : ''}
                                </span>
                              </div>
                              {log.performed_by_email && (
                                <p className="text-xs text-slate-400 mt-1">
                                  By: {log.performed_by_email}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>
          )}

          {/* BILLING TAB */}
          <TabsContent value="billing">
            <div className="space-y-6">
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2"
                             style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Sparkles className="w-5 h-5 text-teal-700" />
                    Subscription & Slots
                  </CardTitle>
                  <CardDescription>
                    Manage your plan and pursuit slot capacity.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Current Plan</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const ws = window.location.pathname.split('/')[1] || '';
                          window.location.href = ws ? `/${ws}/billing/subscribe` : '/billing/subscribe';
                        }}
                        className="border-teal-200 text-teal-700 hover:bg-teal-50"
                        data-testid="upgrade-plan-btn"
                      >
                        Upgrade / Change Plan
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Label>Pursuit Slots</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsSlotTopUpOpen(true)}
                        className="border-teal-200 text-teal-700 hover:bg-teal-50"
                        data-testid="open-slot-topup-btn"
                      >
                        <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                        Buy More Slots
                      </Button>
                    </div>
                  </div>

                  <div className="border-t border-slate-200 pt-5">
                    <Label className="mb-2 block">Self-Serve Billing</Label>
                    <p className="text-sm text-slate-600 mb-3">
                      Update your card, view past invoices, or cancel directly in Stripe&apos;s secure portal.
                    </p>
                    <CustomerPortalButton testId="open-customer-portal-btn" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
        </div>
      </div>
      <SlotTopUpModal open={isSlotTopUpOpen} onOpenChange={setIsSlotTopUpOpen} />
    </div>
  );
}
